
import React, { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, ChevronDown, ChevronRight, MapPin, Church, Filter, Globe } from 'lucide-react';
import { fetchParishes } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';
import ImageHelper from '../lib/imageHelper';
import SeoHead from '@/components/Seo';
import { motion, AnimatePresence } from 'framer-motion';

// ... (Interface remains the same)

interface Parish {
  id: number;
  name: string;
  location: string;
  archdeaconry: string;
  slug: string;
  photo?: string;
}

const Parishes = () => {
  const [parishes, setParishes] = useState<Parish[]>([]);
  const [filteredParishes, setFilteredParishes] = useState<Parish[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterArchdeaconry, setFilterArchdeaconry] = useState('all');
  const [expandedArchdeaconries, setExpandedArchdeaconries] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const loadParishes = async () => {
      try {
        const response = await fetchParishes();
        const transformed = response.data.parishes.map((item: any) => ({
          id: item.id,
          name: item.name,
          location: item.location ?? 'Ibadan, Nigeria',
          archdeaconry: item.archdeaconry?.name ?? 'Diocesan',
          slug: item.slug,
          photo: item?.photo
        }));
        setParishes(transformed);
        setFilteredParishes(transformed);
      } catch (error) {
        toast({ title: 'Connection Error', description: 'Could not reach the parish directory.', variant: 'destructive' });
      } finally {
        setLoading(false);
      }
    };
    loadParishes();
  }, [toast]);

  useEffect(() => {
    const filtered = parishes.filter(parish => {
      const matchesSearch = parish.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        parish.location.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesArch = filterArchdeaconry === 'all' || parish.archdeaconry === filterArchdeaconry;
      return matchesSearch && matchesArch;
    });
    setFilteredParishes(filtered);
  }, [parishes, searchTerm, filterArchdeaconry]);

  const archdeaconries = [...new Set(parishes.map(p => p.archdeaconry))];

  useEffect(() => {
    const updated: Record<string, boolean> = {};
    archdeaconries.forEach(name => { updated[name] = true; });
    setExpandedArchdeaconries(updated);
  }, [parishes]);

  const groupedParishes = filteredParishes.reduce((acc, parish) => {
    if (!acc[parish.archdeaconry]) acc[parish.archdeaconry] = [];
    acc[parish.archdeaconry].push(parish);
    return acc;
  }, {} as Record<string, Parish[]>);

  const toggleExpand = (archdeaconry: string) => {
    setExpandedArchdeaconries(prev => ({ ...prev, [archdeaconry]: !prev[archdeaconry] }));
  };

  // --- SEO BLOCK ---
  const seoContent = (
    <SeoHead
      title="Parish Directory | Ibadan South Diocese"
      description={`Find and connect with any of our ${parishes.length > 0 ? parishes.length : 'vibrant'} parishes. Locate worship centers across all archdeaconries in Ibadan South.`}
      ogImage="https://res.cloudinary.com/dwrqyrm84/image/upload/v1750874183/parish_directory_og.jpg"
    />
  );

  if (loading) return (
    <>
      {seoContent}
      <div className="min-h-screen flex items-center justify-center bg-[#fcfaf7]">
        <div className="flex flex-col items-center">
          <div className="h-12 w-12 border-4 border-amber-100 border-t-amber-600 rounded-full animate-spin mb-6"></div>
          <p className="font-serif italic text-slate-400">Locating our churches...</p>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[#fcfaf7] py-24">
      {seoContent}
      <div className="max-w-7xl mx-auto px-6">

        {/* Page Header */}
        <div className="text-center mb-20">
          <span className="text-amber-600 font-black tracking-[0.4em] uppercase text-[10px] mb-4 block">The Fellowship</span>
          <h1 className="text-5xl lg:text-7xl font-serif font-bold text-slate-900 mb-8">Parish <span className="text-amber-600">Directory</span></h1>
          
          {/* Elegant Control Bar */}
          <div className="max-w-3xl mx-auto bg-white p-2 rounded-2xl shadow-xl shadow-slate-200/50 ring-1 ring-slate-100 flex flex-col sm:flex-row items-center gap-2">
            <div className="relative flex-1 w-full">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-amber-600" />
              <Input
                placeholder="Find a parish or location..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="border-none focus-visible:ring-0 pl-11 h-12 bg-transparent text-slate-700 placeholder:text-slate-300 font-serif"
              />
            </div>
            <div className="hidden sm:block h-6 w-[1px] bg-slate-100"></div>
            <div className="flex items-center w-full sm:w-auto pr-2">
              <Filter className="h-4 w-4 text-slate-400 ml-3 mr-1" />
              <Select onValueChange={setFilterArchdeaconry} value={filterArchdeaconry}>
                <SelectTrigger className="border-none focus:ring-0 bg-transparent font-black text-[10px] uppercase tracking-widest text-slate-500 w-full sm:w-48">
                  <SelectValue placeholder="All Areas" />
                </SelectTrigger>
                <SelectContent className="rounded-xl border-slate-100">
                  <SelectItem value="all" className="text-xs uppercase font-bold">All Archdeaconries</SelectItem>
                  {archdeaconries.map(a => <SelectItem key={a} value={a} className="text-xs">{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Grouped Content */}
        {Object.entries(groupedParishes).map(([arch, archParishes], idx) => {
          const isExpanded = expandedArchdeaconries[arch];
          return (
            <motion.div 
              key={arch} 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
              className="mb-16"
            >
              <button
                onClick={() => toggleExpand(arch)}
                className="w-full flex items-center justify-between group py-6 border-b border-slate-200 mb-8"
              >
                <div className="flex items-baseline gap-4">
                  <h2 className="text-3xl font-serif font-bold text-slate-800 group-hover:text-amber-700 transition-colors">
                    {arch}
                  </h2>
                  <span className="text-amber-600/40 font-black text-[10px] uppercase tracking-widest">
                    {archParishes.length} {archParishes.length === 1 ? 'Parish' : 'Parishes'}
                  </span>
                </div>
                <div className={`transition-all duration-500 p-2 rounded-full ${isExpanded ? 'rotate-180 bg-slate-900 text-white' : 'bg-slate-50 text-slate-400'}`}>
                  <ChevronDown className="h-5 w-5" />
                </div>
              </button>

              <AnimatePresence>
                {isExpanded && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 overflow-hidden"
                  >
                    {archParishes.map(parish => (
                      <Card key={parish.id} className="group border-none shadow-none bg-transparent">
                        <Link to={`/parishes/${parish.slug}`} className="block">
                          <div className="relative aspect-[16/10] rounded-[2rem] overflow-hidden mb-5 ring-1 ring-slate-100 shadow-sm transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-amber-900/10 group-hover:-translate-y-1">
                            <ImageHelper
                              src={parish.photo}
                              alt={parish.name}
                              className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-8">
                              <span className="text-amber-400 text-[10px] font-black uppercase tracking-[0.3em] flex items-center">
                                View Parish Details <ChevronRight className="h-3 w-3 ml-2" />
                              </span>
                            </div>
                          </div>
                          <h3 className="text-2xl font-serif font-bold text-slate-900 group-hover:text-amber-700 transition-colors mb-2">
                            {parish.name}
                          </h3>
                          <div className="flex items-center text-slate-400 text-[10px] font-black uppercase tracking-widest">
                            <MapPin className="h-3 w-3 mr-2 text-amber-500/50" />
                            <span className="truncate">{parish.location}</span>
                          </div>
                        </Link>
                      </Card>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}

        {filteredParishes.length === 0 && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            className="text-center py-32 bg-white rounded-[3rem] border border-slate-100 shadow-sm"
          >
            <div className="bg-slate-50 h-20 w-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Church className="h-8 w-8 text-slate-200" />
            </div>
            <h3 className="text-2xl font-serif font-bold text-slate-800 mb-2">No Parishes Found</h3>
            <p className="text-slate-400 italic font-serif">We couldn't find any churches matching "{searchTerm}" in the selected area.</p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Parishes;